// Copyright (c) Docugami, Inc. All rights reserved.
import { Icon, IconButton, ProgressIndicator, Toggle, TooltipHost, MessageBar, MessageBarType, IMessageBarStyles, Link } from '@fluentui/react';
import { DeepPartial } from '@reduxjs/toolkit';
import { LargeUIBold, SmallLabel, SmallBoldLabel, ExplorerStatusItemLabel, LargeHeading, UserContent } from 'components/common/Fonts';
import { LimitsContainer } from 'components/common/LimitsContainer';
import { themeBlue, homeGray, primaryTheme, appContentColor, landingPageBackground, modalShadow } from 'components/shell/presentation/AppStyles';
import { ConfirmationDialog } from 'components/shell/utils/ConfirmationDialog';
import { Config } from 'config';
import { useAccountView } from 'hooks/AccountView';
import { RequestState, useAsyncHelper } from 'hooks/AsyncHelper';
import { useDeepLinkInitializer } from 'hooks/deepLinkInitializer';
import { useDgAuth } from 'hooks/DgAuthentication';
import { useNavigationService } from 'hooks/navigationService';
import { usePageLimits } from 'hooks/PageLimits';
import { useUserManagement } from 'hooks/UserManagement';
import { memo, useEffect, useState } from 'react';
import { SubscriptionType } from 'networking/OrganizationApi';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import { selectIsActivePaidSubscription, maxUsersThreshold, selectIsLimitedOrganization, selectIsTrialSubscription, getFriendlyNameForSubscriptionType, selectHasSubscriptionHistory } from 'store/AccountInfoHelpers';
import styled from 'styled-components';
import { TiersView, customSubscriptionLevel } from './BillingPage';
import { TelemetryPageView } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { useAppSelector } from 'hooks/redux';
import { redirectToCustomerPortal } from 'billing/BillingService';

const supportUrl = "https://www.docugami.com/support?hsLang=en";

const LandingPage = styled.div`
    box-sizing: border-box;
    flex: 1;
    display: flex;
    gap: 30px;
    position: relative;
    justify-content: center;
    overflow: auto;
    background-color: ${landingPageBackground};
`;

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 40px;
    width: 100%;
    align-items: center;
    gap: 40px;
    margin: 0 auto;
    ${LargeHeading} {
        text-align: center;
    }
`;

const PageContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 25px;
    padding-bottom: 20px;
`;

const TilesContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(0, 480px));
    grid-auto-flow: row;
    justify-content: center;
    gap: 25px;
    width: 100%;
    height: 100%;
    overflow: visible;
    height: fit-content;
`;

const FloatContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    min-height: 500px;
    max-height: fit-content;
    text-align: center;
    z-index: 10;
    background-color: white;
    border-radius: 5px;
    padding: 20px;
    box-sizing: border-box;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.35);
    overflow: auto;
    position: absolute;
    margin: auto;
    top: 0; left: 0; bottom: 0; right: 0;
`;

const TitleText = styled(LargeHeading)`
    color: ${homeGray};
    font-size: 36px;
    font-weight: 500;
`;

export const AccountPage = (): JSX.Element => {
    useDeepLinkInitializer();
    const organizationId = useSelector((state: AppState) => state.organizations.currentOrganization?.id);
    const organizationName = useSelector((state: AppState) => state.organizations.currentOrganization?.name);
    const isAccountLoading = useSelector((state: AppState) => !state.organizations.organizationAccount);
    const isOrganizationExpired = useSelector((state: AppState) => selectIsLimitedOrganization(state));
    const isActiveSubscriber = useSelector((state: AppState) => selectIsActivePaidSubscription(state));
    const hasSubscriptionHistory =  useSelector((state: AppState) => selectHasSubscriptionHistory(state));

    const { subscriptionType, expirationDate, maxUsersCount, pagesCount, maxPages, pageLimitThresholdReached } = useAccountView();
    const { users, addUsersModal, showAddUsersModal, canRemoveUser, removeUserModal, setRemoveUserEmail } = useUserManagement(organizationId);
    const isCustomSubscriber = subscriptionType === customSubscriptionLevel;
    const isTrialUser = subscriptionType === SubscriptionType.Trial;

    const { navToSubscriptionUpgradePage } = useNavigationService();
    const { isOrganizationAdmin, user } = useDgAuth();

    const [viewTiersModal, setViewTiersModal] = useState(false);

    useEffect(() => {
        Telemetry.trackPageView(TelemetryPageView.Account);
    }, []);

    function tiersModal() {
        if (!viewTiersModal) {
            return;
        }

        // TODO this should be replaced with a fluent modal
        return (
            <FloatContainer>
                <TitleText>Subscription Tiers</TitleText>
                <TiersView readOnly onDismiss={() => setViewTiersModal(false)} />
            </FloatContainer>
        );
    }

    function getSubscriptionInfoLinks() {
        if (!isOrganizationAdmin || isAccountLoading) {
            return [];
        }

        if (isCustomSubscriber) {
            return [
                {
                    name: "Contact Us",
                    onClick: () => window.open(Config.contactUsUrl, '_blank')
                },
                {
                    name: "View Tiers",
                    onClick: () => setViewTiersModal(true)
                }
            ]
        }

        if (isTrialUser) {
            return [
                {
                    name: "Upgrade",
                    onClick: navToSubscriptionUpgradePage
                }
            ]
        }

        if (isActiveSubscriber) {
            return [
                {
                    name: "Manage Subscription",
                    onClick: navToSubscriptionUpgradePage
                },
                {
                    name: "View Tiers",
                    onClick:  () => setViewTiersModal(true)
                }
            ]
        }

        if (hasSubscriptionHistory) {
            return [
                {
                    name: "View Billing History",
                    onClick: () => redirectToCustomerPortal(organizationId)
                },
                {
                    name: "Upgrade",
                    onClick: navToSubscriptionUpgradePage
                }
            ]
        }

        return [];
    }

    return (
        <>
            <LandingPage>
                <PageContainer>
                    <LargeHeading>Manage Account</LargeHeading>
                    <PageContentContainer>
                        <AccountWarningsMessageBar />
                        <TilesContainer>
                            <ProfileTile
                                email={user?.email ?? ''}
                                organizationName={organizationName ?? ''}
                                name={user?.name ?? ''}
                                links={[]} />
                            <SubscriptionInfoTile
                                subscriptionType={subscriptionType}
                                dateDescriptor={isOrganizationExpired ? 'Expired' : isActiveSubscriber ? 'Renews' : 'Expires'}
                                expirationDate={expirationDate}
                                links={getSubscriptionInfoLinks()} />
                            <PagesInfoTile
                                numPages={pagesCount}
                                maxPages={maxPages}
                                links={[{
                                    name: 'Upgrade to increase your pages and unlock new features',
                                    onClick: navToSubscriptionUpgradePage,
                                    hidden: !pageLimitThresholdReached || !isOrganizationAdmin || isCustomSubscriber
                                }]} />
                            <UsersInfoTile
                                users={users}
                                maxUsersCount={maxUsersCount}
                                addUsersCallback={showAddUsersModal}
                                canRemoveUser={canRemoveUser}
                                showRemoveUserModal={setRemoveUserEmail}
                                links={[{
                                    name: 'Upgrade to collaborate and add new users to your team',
                                    onClick: navToSubscriptionUpgradePage,
                                    hidden: !users?.length || !maxUsersCount || !maxUsersThreshold(users.length, maxUsersCount) || !isOrganizationAdmin || isCustomSubscriber
                                }]} />
                        </TilesContainer>
                    </PageContentContainer>
                </PageContainer>
                {tiersModal()}
            </LandingPage>
            {addUsersModal}
            {removeUserModal}
        </>
    );
}

const AccountWarningsMessageBar = (): JSX.Element | null => {
    const isLimitedOrg = useAppSelector((state) => selectIsLimitedOrganization(state));
    const isTrialOrg = useAppSelector((state) => selectIsTrialSubscription(state));
    const { pageUploadDisabled } = usePageLimits();
    const { isOrganizationAdmin } = useDgAuth();
    const navService = useNavigationService();

    function getActionableMessage() {
        // If we are loading user info, show no actionable text
        if (isOrganizationAdmin === undefined) {
            return;
        }

        if (isOrganizationAdmin) {
            return <>Click <Link onClick={navService.navToSubscriptionUpgradePage} underline>here</Link> to upgrade.</>
        } else {
            return <>Please contact your workspace admin to upgrade your {isTrialOrg ? 'free trial' : 'subscription'} or click<Link target="_blank" rel="noopener noreferrer" href={supportUrl} underline>here</Link> to contact support.</>
        }
    }

    const messageBarStyle: DeepPartial<IMessageBarStyles> = {
        text: {
            fontSize: "13px"
        }
    }

    if (isLimitedOrg) {
        return <MessageBar styles={messageBarStyle} isMultiline messageBarType={MessageBarType.severeWarning}>Your {isTrialOrg ? 'free trial' : 'subscription'} has expired. {getActionableMessage()}</MessageBar>
    }

    if (pageUploadDisabled) {
        return <MessageBar styles={messageBarStyle} isMultiline messageBarType={MessageBarType.warning}>You have reached your page usage limit. {getActionableMessage()}</MessageBar>
    }

    return null;
}

interface InfoTileProps {
    links: AccountInfoLink[];
}

const InfoContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 10%;
    overflow: hidden;
    margin-top: 5%;
`;

const TextContentContainer = styled(InfoContentContainer)`
    justify-content: flex-start;
`;

const MFAContainer = styled.div<{ hidden?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    ${props => !!props.hidden && 'visibility: hidden'};
`;

interface ProfileTileProps extends InfoTileProps {
    email: string;
    organizationName: string;
    name?: string;
}

const ProfileTile = (props: ProfileTileProps): JSX.Element => {
    const [showMfaConfirmation, setShowMfaConfirmation] = useState(false);
    const { isUserNamePassword, isMultiFactorAuth, setMultiFactorAuth, logout, requestPasswordReset } = useDgAuth();
    const [mfaToggleChecked, setMfaToggleChecked] = useState<boolean | undefined>(undefined);
    const [emailResetMessage, setEmailResetMessage] = useState<string | undefined>(undefined);
    const [requestingEmailReset, setRequestingEmailReset] = useState(false);
    const { execute, state } = useAsyncHelper("Flip Mfa");

    useEffect(() => {
        setMfaToggleChecked(isMultiFactorAuth);
    }, [isMultiFactorAuth])

    async function flipMfa() {
        setShowMfaConfirmation(false);
        const success = await setMultiFactorAuth(!isMultiFactorAuth);
        if (success) {
            logout({ returnTo: `${Config.baseUrl}` });
        }
    }

    function flipMultiFactorAuthConfirmationDialog(): JSX.Element {
        function onDismiss() {
            // Undo toggle change
            setMfaToggleChecked(isMultiFactorAuth);
            setShowMfaConfirmation(false);
        }

        return (
            state === RequestState.InProgress ? <ProgressIndicator /> :
                <ConfirmationDialog
                    hidden={!showMfaConfirmation}
                    title="Please Confirm"
                    subText="After changing your Multifactor Authentication setting, Docugami will log you out and require you to log back in."
                    confirmButtonText="Continue"
                    dismissButtonText="Cancel"
                    confirmedCallback={() => execute(flipMfa())}
                    dismissedCallback={onDismiss} />
        )
    }

    function onToggleChange(ev: any, checked?: boolean) {
        setMfaToggleChecked(!!checked);
        setShowMfaConfirmation(true);
    }

    async function requestPasswordResetClicked() {
        setRequestingEmailReset(true);
        const message = await requestPasswordReset();
        setEmailResetMessage(message);
    }

    function onClosePasswordResetDismissDialog() {
        setEmailResetMessage(undefined);
        setRequestingEmailReset(false);
    }

    function passwordResetResponseDialog(): JSX.Element {
        return (
            <ConfirmationDialog
                hidden={!emailResetMessage}
                title="Password Reset Request"
                subText={emailResetMessage ?? ''}
                confirmButtonText="OK"
                confirmedCallback={onClosePasswordResetDismissDialog}
                dismissedCallback={onClosePasswordResetDismissDialog} />
        )
    }

    const links = [...props.links];
    if (isUserNamePassword) {
        links.push({
            name: 'Reset Password',
            onClick: requestPasswordResetClicked,
            disabled: requestingEmailReset
        });
    }

    return (
        <>
            <AccountInfoTile title={"Profile"} links={links}>
                <TextContentContainer>
                    <div key='email'>
                        <SmallLabel>Email: </SmallLabel>
                        <SmallBoldLabel>{props.email}</SmallBoldLabel>
                    </div>
                    <div key='name'>
                        <SmallLabel key="name_key">Name: </SmallLabel>
                        <SmallBoldLabel key="name_value">{props.name}</SmallBoldLabel>
                    </div>
                    <div key='organization'>
                        <SmallLabel>Workspace: </SmallLabel>
                        <SmallBoldLabel>{props.organizationName}</SmallBoldLabel>
                    </div>
                    <MFAContainer key='enable_mfa' hidden={!isUserNamePassword}>
                        <SmallLabel>Enable Multi-Factor Authentication: </SmallLabel>
                        <Toggle
                            key='MFA_Toggle'
                            disabled={isMultiFactorAuth === undefined || state === RequestState.InProgress}
                            checked={!!mfaToggleChecked}
                            onChange={onToggleChange}
                            styles={{ root: { marginBottom: '0' } }} />
                    </MFAContainer>
                </TextContentContainer>
                {flipMultiFactorAuthConfirmationDialog()}
                {passwordResetResponseDialog()}
            </AccountInfoTile>
        </>
    );
}

interface SubscriptionInfoTileProps extends InfoTileProps {
    subscriptionType?: SubscriptionType;
    dateDescriptor: string;
    expirationDate?: Date;
}

const SubscriptionInfoTile = (props: SubscriptionInfoTileProps): JSX.Element => {
    const isTrialOrg = useAppSelector((state) => selectIsTrialSubscription(state));

    return (
        <AccountInfoTile title={"Subscription"} links={props.links}>
            <TextContentContainer>
                <div key='type'>
                    <SmallLabel>Type: </SmallLabel>
                    <SmallBoldLabel>{getFriendlyNameForSubscriptionType(props.subscriptionType).toLocaleUpperCase() ?? 'N/A'}</SmallBoldLabel>
                </div>
                <div key='subscription_cycle_end'>
                    <SmallLabel>{`${props.dateDescriptor}: `}</SmallLabel>
                    <SmallBoldLabel>{props.expirationDate?.toDateString() ?? 'N/A'}</SmallBoldLabel>
                    <TooltipHost
                        content={
                            isTrialOrg ?
                            "Your account and all associated data will be automatically deleted 2 weeks after expiration" :
                            "Your account and all associated data will be automatically deleted 90 days after expiration"
                        } >
                        <Icon iconName="infoSolid" style={{ paddingLeft: '10px' }}/>
                    </TooltipHost>
                </div>
            </TextContentContainer>
        </AccountInfoTile>
    );
}

interface PagesInfoTileProps extends InfoTileProps {
    numPages?: number | null;
    maxPages?: number;
    hidden?: boolean;
    progressIsInfinite?: boolean;
}

const PagesInfoTile = (props: PagesInfoTileProps): JSX.Element => {
    if (!!props.hidden) {
        return <></>;
    }

    return (
        <AccountInfoTile title={"Pages"} links={props.links}>
            {(!!props.numPages || props.numPages === 0) &&
                <InfoContentContainer>
                    <AccountInfoProgressBar
                        count={props.numPages}
                        total={props.maxPages}
                        itemType={'pages consumed'}
                        isInfinite={props.progressIsInfinite} />
                </InfoContentContainer>
            }
        </AccountInfoTile>
    );
}

const AddUsersButton = styled.button`
    display: flex;
    margin-left: auto;
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    align-items: center;
    gap: 5px;
    background: none;
    border: none;
    color: ${primaryTheme.palette.black};

    .disabled {
        ${props => props.disabled && 'opacity: 20%'};
    }
`;

const UsersList = styled.ul`
    column-count: 2;
    list-style-type: none;
    padding: 0;
    margin: 0;
    height: 100%;
    overflow: auto;
`;

const UserListItem = styled.li`
    margin-bottom: 5px;
    display: flex;
    flex-direction: row;
    gap: 3px;
    align-items: baseline;

    :hover button {
        visibility: visible;
    }
`;

const UserListItemText = styled(ExplorerStatusItemLabel)`
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

interface UsersInfoTileProps extends InfoTileProps {
    users: string[] | null;
    maxUsersCount?: number;
    addUsersCallback: () => void;
    canRemoveUser: (user: string) => boolean;
    showRemoveUserModal: (user: string) => void;
}

const UsersInfoTile = (props: UsersInfoTileProps): JSX.Element => {
    const usersCount = props.users?.length ?? 0;
    const canAddUsers = !props.maxUsersCount ? true : usersCount < props.maxUsersCount;
    const { isOrganizationAdmin } = useDgAuth();

    const closeButtonStyles = {
        root: {
            height: "10px",
            width: "15px",
            visibility: "hidden"
        },
        rootHovered: {
            background: 'transparent',
            transform: 'scale(1.1)'
        },
        rootPressed: {
            background: 'transparent',
            transform: 'scale(1)'
        },
        icon: {
            color: homeGray,
            fontSize: "10px"
        }
    }

    return (
        <AccountInfoTile title={"Users"} links={props.links}>
            <InfoContentContainer>
                <AccountInfoProgressBar count={usersCount} total={props.maxUsersCount} itemType={'users'} />
                <LimitsContainer actionName='add users' >
                    <AddUsersButton key='add_users' disabled={!canAddUsers || !isOrganizationAdmin} onClick={props.addUsersCallback}>
                        {!canAddUsers && <Icon iconName={'LockSolid'} />}
                        <SmallBoldLabel className="disabled" style={{ fontSize: '16px' }}>ADD USER</SmallBoldLabel>
                        <Icon className="disabled" iconName="CirclePlus" />
                    </AddUsersButton>
                </LimitsContainer>
                <UsersList key='users_list'>
                    {
                        props.users?.map(user =>
                            <UserListItem key={user} title={user}>
                                <UserListItemText>{user}</UserListItemText>
                                {(props.canRemoveUser(user) && isOrganizationAdmin) &&
                                    <IconButton
                                        title="Remove User"
                                        styles={closeButtonStyles}
                                        iconProps={{ iconName: "ChromeClose" }}
                                        onClick={() => props.showRemoveUserModal(user)} />}
                            </UserListItem>
                        )
                    }
                </UsersList>
            </InfoContentContainer>
        </AccountInfoTile>
    );
}

interface AccountInfoLink {
    name: string;
    onClick: () => void;
    hidden?: boolean;
    disabled?: boolean;
}

interface AccountInfoTileProps {
    title: string;
    children?: JSX.Element | JSX.Element[] | null | boolean;
    links?: AccountInfoLink[];
}

const InfoTileContainer = styled.div`
    height: 320px;
    width: 100%;
    box-shadow: ${modalShadow};
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
    box-sizing: border-box;
    background-color: ${primaryTheme.palette.white};
`;

export const TileLink = styled(SmallBoldLabel)`
    color: ${themeBlue};
    font-size: 16px;
    cursor: pointer;

    :hover {
        text-decoration: underline;
    }
`;

const AccountInfoTitle = styled(LargeUIBold)`
    line-height: 40px;
`;

const ContentContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
    flex: 1;
    min-height: 0;
`;

const LinksContainer = styled.div`
    display: flex;
    justify-content: space-evenly;
    width: 100%;
`;

const AccountInfoTile = (props: AccountInfoTileProps): JSX.Element => {
    return (
        <InfoTileContainer>
            <AccountInfoTitle>{props.title}</AccountInfoTitle>
            <ContentContainer>{props.children}</ContentContainer>
            <LinksContainer key='links'>
                {
                    props.links?.map(link => !link.hidden && <TileLink key={link.name} onClick={link.onClick}>{link.name}</TileLink>)
                }
            </LinksContainer>
        </InfoTileContainer>
    )
}

const AltText = styled(UserContent)`
    font-size: 20px;
    font-weight: 800;
    color: ${appContentColor};
    margin-top: 10px;
`;

interface AccountInfoProgressBarProps {
    count: number;
    total?: number;
    itemType: string;
    isInfinite?: boolean;
}

const AccountInfoProgressBar = memo((props: AccountInfoProgressBarProps): JSX.Element => {
    if (props.count === 0 && !props.total) {
        // We have 0 out of 0 items, no need to show progress bar
        return <AltText>{`0 ${props.itemType.toLocaleUpperCase()}`}</AltText>
    }

    const progressIndicatorStyle = {
        progressBar: {
            background: 'linear-gradient(180deg, #1FBDF0 0%, #8BDEF9 100%)',
            borderRadius: '7px'
        },
        progressTrack: {
            borderRadius: '7px'
        },
        root: {
            fontWeight: "800",
            fontSize: '16px'
        }
    }

    const percentComplete = !!props.isInfinite ? undefined : props.total ? props.count / props.total : 1;
    const countLabel = props.total ? `${props.count.toLocaleString()} of ${props.total.toLocaleString()}` : `${props.count.toLocaleString()}`;

    return <ProgressIndicator barHeight={13} styles={progressIndicatorStyle} percentComplete={percentComplete} label={`${countLabel} ${props.itemType.toLocaleUpperCase()}:`} />
})
